/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Authentication error handler
 *
 * Reacts once, at application level, to normalized authentication errors so
 * HTTP interceptors and identity-provider adapters stay free of navigation.
 */

import {DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {datadogLogs} from '@datadog/browser-logs';
import {catchError, EMPTY, exhaustMap, filter, from} from 'rxjs';

import {
  AUTHENTICATION_PROVIDER,
  IAuthenticationError,
  LOGIN_REQUIRED_ERROR_CODE,
} from './authentication-provider.token';

/** Application route shown for authentication errors that need no new login. */
export const AUTHENTICATION_ERROR_ROUTE = '/error';

/**
 * Subscribes to the active authentication provider's `error$` stream and
 * routes the user according to the error kind.
 *
 * - Missing or expired sessions ({@link LOGIN_REQUIRED_ERROR_CODE}) restart the
 *   interactive login with the current route as the post-login return route.
 * - Every other authentication error opens {@link AUTHENTICATION_ERROR_ROUTE}.
 *
 * Errors that arrive while a previous error is still being handled are
 * swallowed, so several protected requests failing at once trigger a single
 * login redirect. A failed login or navigation attempt is logged and does not
 * stop later errors from being handled.
 */
@Injectable({providedIn: 'root'})
export class AuthenticationErrorHandlerService {
  private readonly authenticationProvider = inject(AUTHENTICATION_PROVIDER);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private initialized = false;

  /**
   * Starts listening for authentication errors. Subsequent calls are ignored.
   */
  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.authenticationProvider.error$
      .pipe(
        filter((error): error is IAuthenticationError => error !== null),
        exhaustMap((error) =>
          from(this.handleAuthenticationError(error)).pipe(
            catchError((handlingError: unknown) => {
              datadogLogs.logger.error(
                'Authentication error handling failed',
                {authenticationError: error},
                handlingError instanceof Error ? handlingError : undefined,
              );
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  /**
   * Reacts to a single normalized authentication error.
   *
   * @param error - The normalized authentication error.
   * @returns A promise that resolves after the navigation or login redirect started.
   */
  async handleAuthenticationError(error: IAuthenticationError): Promise<void> {
    if (error.code === LOGIN_REQUIRED_ERROR_CODE) {
      await this.authenticationProvider.login({
        returnUrl: this.resolveReturnUrl(),
      });
      return;
    }

    await this.router.navigateByUrl(AUTHENTICATION_ERROR_ROUTE);
  }

  /**
   * Resolves the application route to return to after a new login.
   *
   * During application startup `Router.url` is still `/` because the initial
   * navigation has not completed yet, which would lose the requested deep
   * link. The route is therefore taken from the pending navigation when one
   * exists, then from the browser location while the router still reports the
   * root route, and only otherwise from the router itself.
   *
   * @returns The application-relative route to open after the login.
   */
  private resolveReturnUrl(): string {
    const finalUrl = this.router.getCurrentNavigation()?.finalUrl;
    if (finalUrl) {
      return this.router.serializeUrl(finalUrl);
    }

    const routerUrl = this.router.url;
    if (!routerUrl || routerUrl === '/') {
      return `${window.location.pathname}${window.location.search}${window.location.hash}`;
    }

    return routerUrl;
  }
}
